// =============================================================================
// Shared building blocks for dashboard widget bodies.
//
// A widget body is a React component rendered inside the widget card's
// content area (the card chrome, header and scroll container are provided by
// Dashboard.js). Every body receives the same props:
//
//   data       — the full app data tree (read-only)
//   updateData — (partial) => void, for widgets that act (tick off a task…)
//   scope      — the chosen instance id (calendar id, list id, 'personal',
//                'all', 'total', …) or null for single-instance widgets
//   size       — { w, h } current grid size, so a body can render compactly
//                when small; `h` is in 44px rows
//   today      — Date, refreshed every minute by Dashboard
//   t, lang    — i18n helpers
//
// Bodies must be resilient to empty/legacy data: never assume an array or a
// nested object exists.
// =============================================================================

import React from 'react';

// Money is stored as plain numbers; the app formats German/EUR throughout
// (mirrors formatCurrency in Budget.js).
export const formatEUR = (amount) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
    Number.isFinite(amount) ? amount : 0
  );

// Compact variant for stat tiles where space is tight (1.234 € instead of
// 1.234,56 €).
export const formatEURShort = (amount) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(Number.isFinite(amount) ? amount : 0);

export const formatHours = (seconds) => {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0) return `${m} min`;
  return `${h}:${String(m).padStart(2, '0')} h`;
};

// Centered muted message for "nothing to show".
export const WidgetEmpty = ({ children }) => (
  <p className="text-sm text-gray-500 dark:text-gray-400 px-4 py-6 text-center">
    {children}
  </p>
);

const TONES = {
  default: 'text-gray-900 dark:text-gray-100',
  positive: 'text-green-600 dark:text-green-400',
  negative: 'text-red-600 dark:text-red-400',
  accent: 'text-primary-600 dark:text-primary-400',
  muted: 'text-gray-500 dark:text-gray-400'
};

// Big single-number tile — the "small box" widget shape (e.g. money left
// this month, hours this week). Scales its type down in short widgets.
export const StatTile = ({ value, label, sub, tone = 'default', icon: Icon, compact }) => (
  <div className="h-full w-full flex flex-col items-center justify-center px-3 py-2 text-center min-w-0">
    {Icon && <Icon size={compact ? 16 : 20} className="mb-1 text-gray-400 flex-shrink-0" />}
    <div
      className={`font-bold tabular-nums leading-tight truncate max-w-full ${
        compact ? 'text-xl' : 'text-3xl'
      } ${TONES[tone] || TONES.default}`}
      title={typeof value === 'string' ? value : undefined}
    >
      {value}
    </div>
    {label && (
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-full">
        {label}
      </div>
    )}
    {sub && (
      <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 truncate max-w-full">
        {sub}
      </div>
    )}
  </div>
);

// Row list used by most list-style widgets.
export const WidgetList = ({ children }) => (
  <ul className="divide-y divide-gray-100 dark:divide-gray-800">{children}</ul>
);

export const WidgetRow = ({ children, onClick }) => (
  <li
    className={`flex items-center gap-2.5 px-4 py-2 min-w-0 ${
      onClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''
    }`}
    onClick={onClick}
  >
    {children}
  </li>
);

// Thin progress/proportion bar (budget categories, goals…).
export const WidgetBar = ({ pct, color }) => (
  <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
    <div
      className="h-full rounded-full"
      style={{
        width: `${Math.max(0, Math.min(100, pct || 0))}%`,
        backgroundColor: color || 'rgb(var(--color-primary-500))'
      }}
    />
  </div>
);

// True when the widget is too short for comfortable list rendering — bodies
// use it to switch to a condensed presentation.
export const isCompact = (size) => !size || size.h <= 4 || size.w <= 3;
